import { stringify as stringifyYaml } from 'yaml';
import type { ToolsFrontmatter, ToolDefinition, GlobalControls } from '../types/tools.js';

export interface ToolsGenerationInput {
  agent_id: string;
  code_name: string;
  environment: 'dev' | 'stage' | 'prod';
  owner: string;
  display_name: string;
  enforcement_mode?: 'wrapper' | 'gateway' | 'both';
  logging_redaction?: 'hash-only' | 'redacted' | 'full-local';
  global_controls?: Partial<GlobalControls>;
  tools?: ToolDefinition[];
}

/**
 * WHY THE GIT WORKFLOW SECTION BELOW USES A SIBLING-RELATIVE PATH AND NEVER AN
 * ABSOLUTE ONE. The agent's tree is keyed by CODE NAME on some hosts and by
 * AGENT ID on others - ADR-0049's id-keyed migration renames the tree to the
 * uuid and leaves the code name as a compatibility symlink - so a hard-coded
 * agent id is wrong before that migration and a hard-coded code name is wrong
 * after it. `../code/` is correct on both sides of it.
 *
 * AND THE RENDERED RATIONALE IS HEDGED, DELIBERATELY (obi-wan `9e67d4f19686`).
 * It used to assert flatly that the tree "survives a container rebuild" and that
 * `~/code/` "is destroyed by one". Docker isolation is opt-in per HOST and per
 * AGENT - `isolationMode()` returns 'none' unless AGT_ISOLATION is 'docker', and
 * AGT_ISOLATION_AGENTS narrows it to a named subset - so a BYO or self-hosted
 * agent runs in no container at all, nothing is bind-mounted, and `~/code/` is an
 * ordinary durable directory. Stating otherwise hands that agent a false claim
 * about its own environment inside its own system prompt. The measurement behind
 * the original wording was taken on one Docker-isolated host and generalised to
 * the fleet, which is the defect this whole change is about, committed in the
 * sentence explaining the fix. The DIRECTIVE is correct on both sides - `../code/`
 * keeps the checkout inside the agent's own tree either way - so only the reason
 * needed narrowing.
 *
 * THIS RATIONALE LIVES HERE, NOT IN THE TEMPLATE (obi-wan `bbd6ad92b450`,
 * raised twice as `a4fe81cf61ee`). It used to sit inside the returned literal
 * as "Do not spell the tree's absolute path here", which shipped verbatim into
 * every generated TOOLS.md. The reader there is the AGENT, not whoever edits
 * this generator: "here" has no referent for them, and it reads as a directive
 * about their own output - one that flatly contradicts the CLAUDE.md two
 * sections earlier, which does spell the absolute project/ and scratch/ paths.
 * A maintainer's note addressed to nobody in the room is worse than no note.
 */
export function generateToolsMd(input: ToolsGenerationInput): string {
  const today = new Date().toISOString().split('T')[0]!;

  const globalControls: GlobalControls = {
    default_network_policy: input.global_controls?.default_network_policy ?? 'deny',
    default_timeout_ms: input.global_controls?.default_timeout_ms ?? 8000,
    default_rate_limit_rpm: input.global_controls?.default_rate_limit_rpm ?? 60,
    default_retries: input.global_controls?.default_retries ?? 2,
    logging_redaction: input.global_controls?.logging_redaction ?? input.logging_redaction ?? 'redacted',
  };

  const frontmatter: ToolsFrontmatter = {
    agent_id: input.agent_id,
    code_name: input.code_name,
    version: '0.1',
    environment: input.environment,
    owner: input.owner,
    last_updated: today,
    enforcement_mode: input.enforcement_mode ?? 'wrapper',
    global_controls: globalControls,
    tools: input.tools ?? [],
  };

  const yaml = stringifyYaml(frontmatter, { lineWidth: 0 });

  const toolsList = frontmatter.tools.length > 0
    ? frontmatter.tools.map((t) =>
        `- **${t.name}** (\`${t.id}\`): ${t.description} [${t.access}, ${t.limits.timeout_ms}ms, ${t.limits.rate_limit_rpm}rpm]`
      ).join('\n')
    : 'No tools are individually recorded in this manifest. This does not restrict the agent; its live tools come from its runtime provisioning.';

  return `# TOOLS — ${input.display_name}

---
${yaml}---

This manifest is an informational governance record and may be incomplete. Tool access is enforced by the agent's runtime (a gateway where one is deployed, or the runtime's own MCP and permission configuration), not by this document. Secrets via \`secret_ref://\` only.

${toolsList}

## Git Workflow

Use **git worktrees** for all feature work. Do not switch branches on the main checkout.
Store repositories in \`../code/\` - the \`code/\` directory beside the agent's \`project/\` working
directory, inside the agent's own tree - which is what makes it the agent's to keep. Where this
host runs agents in containers that tree is bind-mounted and survives a rebuild, while \`~/code/\`
is container-local and is destroyed by one. Create worktrees alongside the checkout for parallel
tasks.
`;
}
